import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Sun, Moon } from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';
import { preloadRoute } from '@/hooks/useRoutePreload';
import storilyIcon from '@/assets/storily-icon.png';
import storilyIconDark from '@/assets/storily-icon-dark.png';
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuList,
  NavigationMenuTrigger,
  NavigationMenuLink,
} from "@/components/ui/navigation-menu";
import ResourcesMegaMenu from '@/components/sections/header/ResourcesMegaMenu';
import PlatformMegaMenu from '@/components/sections/header/PlatformMegaMenu';
import MobileNavigation from '@/components/sections/header/MobileNavigation';

const EditorialHeader: React.FC = () => {
  const { theme, toggleTheme } = useTheme();
  const [scrollProgress, setScrollProgress] = useState(0);
  // Initialize based on route to prevent hydration mismatch on SSG
  const [isOverDarkHero, setIsOverDarkHero] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.location.pathname === '/agency-owners';
    }
    return false;
  });

  useEffect(() => {
    const handleScroll = () => {
      // Calculate scroll progress from 0 to 1 over the first 100px of scroll
      const progress = Math.min(window.scrollY / 100, 1);
      setScrollProgress(progress);
      
      // Check if we're on agency-owners page (has dark hero)
      if (window.location.pathname === '/agency-owners') {
        const heroHeight = window.innerHeight * 0.9; // 90vh hero
        const headerHeight = 80;
        const currentPosition = window.scrollY + headerHeight;
        
        setIsOverDarkHero(currentPosition < heroHeight);
      } else {
        setIsOverDarkHero(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Run on mount to set initial state
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Use dark styling when in dark theme OR when over a dark hero section
  const isDarkSection = theme === 'dark' || isOverDarkHero;
  
  // Interpolate values based on scroll progress
  const isScrolled = scrollProgress > 0.1;
  const maxWidth = 100 - (scrollProgress * 40); // Goes from 100% to 60%
  const borderRadius = scrollProgress * 12; // Goes from 0 to 12px
  const topPosition = 20 - (scrollProgress * 8); // Goes from 20px to 12px
  const blur = scrollProgress * 12; // Goes from 0 to 12px
  const bgOpacity = scrollProgress * 0.8; // Goes from 0 to 0.8

  return (
    <header 
      className="fixed left-1/2 -translate-x-1/2 z-50"
      style={{ 
        top: `${topPosition}px`,
        backgroundColor: isDarkSection 
          ? `rgba(17, 17, 17, ${bgOpacity})` 
          : `rgba(252, 252, 249, ${bgOpacity})`,
        backdropFilter: `blur(${blur}px)`,
        WebkitBackdropFilter: `blur(${blur}px)`,
        border: `1px solid ${isDarkSection ? `rgba(255, 255, 255, ${scrollProgress * 0.1})` : `rgba(0, 0, 0, ${scrollProgress * 0.05})`}`,
        borderRadius: `${borderRadius}px`,
        width: `${maxWidth}%`,
        maxWidth: '960px',
        minWidth: 'fit-content',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      }}
    >
      <div className="px-6 lg:px-8 py-2 flex items-center justify-between gap-8">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-1">
          <span 
            className="font-semibold text-xl tracking-tight transition-all duration-300"
            style={{ 
              color: isDarkSection ? '#FFFFFF' : '#111111',
              opacity: 1 - (scrollProgress * 2),
              transform: `translate(${scrollProgress * 8}px, ${scrollProgress * -6}px)`,
              maxWidth: isScrolled ? 0 : '100px',
              overflow: 'hidden',
              whiteSpace: 'nowrap',
            }}
          >
            Storily
          </span>
          <img 
            src={isDarkSection ? storilyIconDark : storilyIcon} 
            alt="Storily" 
            width={20}
            height={20}
            className="transition-all duration-300"
            style={{ height: '20px', width: 'auto' }}
          />
        </Link>

        {/* Navigation - Desktop */}
        <NavigationMenu className="hidden lg:flex items-center mx-auto">
          <NavigationMenuList className="space-x-4">
            {/* Platform Mega Menu */}
            <NavigationMenuItem>
              <NavigationMenuTrigger 
                className="text-sm font-medium transition-colors bg-transparent hover:bg-transparent data-[state=open]:bg-transparent"
                style={{ color: isDarkSection ? '#AAAAAA' : '#555555' }}
              >
                Platform
              </NavigationMenuTrigger>
              <NavigationMenuContent>
                <PlatformMegaMenu />
              </NavigationMenuContent>
            </NavigationMenuItem>

            {/* Pricing - Simple Menu Item */}
            <NavigationMenuItem>
              <NavigationMenuLink 
                href="/pricing"
                className="group inline-flex h-10 w-max items-center justify-center px-4 py-2 text-sm font-medium transition-colors bg-transparent hover:bg-transparent"
                style={{ color: isDarkSection ? '#AAAAAA' : '#555555' }}
                onMouseEnter={() => preloadRoute('/pricing')}
              >
                Pricing
              </NavigationMenuLink>
            </NavigationMenuItem>

            {/* Resources Mega Menu */}
            <NavigationMenuItem>
            <NavigationMenuTrigger 
                className="text-sm font-medium transition-colors bg-transparent hover:bg-transparent data-[state=open]:bg-transparent"
                style={{ color: isDarkSection ? '#AAAAAA' : '#555555' }}
              >
                Resources
              </NavigationMenuTrigger>
              <NavigationMenuContent>
                <ResourcesMegaMenu />
              </NavigationMenuContent>
            </NavigationMenuItem>
          </NavigationMenuList>
        </NavigationMenu>

        {/* CTA */}
        <div className="flex items-center gap-2">
          <button
            onClick={toggleTheme}
            className="p-2 rounded-lg transition-colors"
            style={{ 
              color: isDarkSection ? '#AAAAAA' : '#555555',
            }}
            aria-label="Toggle theme"
          >
            {theme === 'dark' ? (
              <Sun className="w-5 h-5" />
            ) : (
              <Moon className="w-5 h-5" />
            )}
          </button>

          {/* Mobile Navigation - mobile only */}
          <div className="block lg:hidden">
            <MobileNavigation isDarkSection={isDarkSection} />
          </div>

          <button 
            className="hidden sm:flex px-4 py-2 text-white text-sm font-medium hover:bg-[#222222] hover:scale-[1.02] hover:shadow-md active:scale-[0.98] transition-all duration-200 items-center gap-2"
            style={{ 
              backgroundColor: '#000000',
              borderRadius: '8px'
            }}
            onClick={() => window.open('https://app.storymarketing.group/', '_blank')}
          >
            Sign In
          </button>
        </div>
      </div>
    </header>
  );
};

export default EditorialHeader;
